import { useEffect, useRef, useState } from "react";
import { CircularProgress, Stack } from "@mui/material";
import type { Promisable, SetOptional } from "type-fest";

import type {
  FormContextData,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";

import { FormSupportingDocumentsSelector } from "./FormSupportingDocumentsSelector.js";

type SelectorProps = Parameters<typeof FormSupportingDocumentsSelector>[0];

/** What a read returned, or `fallback` when it gave nothing or failed. */
async function readOrFallback<T, F>(
  read: () => Promisable<T | null>,
  fallback: F,
): Promise<T | F> {
  try {
    return (await read()) ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * The supporting-documents selector, reading each form's status as it is now
 * rather than as the surrounding page last saw it.
 *
 * A form's redo state can change between the page loading and this selector
 * opening, and only a form that is completed and not being redone may be linked
 * as a basis (`formIsValidForProgression`) — so a status read a moment too early
 * either offers a basis that can no longer be used or withholds one that has
 * become valid again.
 *
 * The form context and timeline passed in are the fallback for a read that
 * fails; the timeline is optional, unlike on the selector itself, because the
 * page may not have loaded one and this reads its own anyway.
 */
export function CurrentSupportingDocumentsSelector({
  getCurrentFormContext,
  getCurrentPatientTimeline,
  formContext,
  patientTimeline,
  ...selectorProps
}: SetOptional<SelectorProps, "patientTimeline"> & {
  getCurrentFormContext: () => Promisable<FormContextData | null>;
  getCurrentPatientTimeline: () => Promisable<PatientTimelineResponse | null>;
}) {
  const [current, setCurrent] = useState<{
    formContext: SelectorProps["formContext"];
    patientTimeline: SelectorProps["patientTimeline"] | undefined;
  }>();

  // Held in a ref so the read cannot be re-triggered by callers passing fresh
  // handler identities on every render: this is a point-in-time read taken when
  // the selector opens, not a subscription.
  const latest = {
    getCurrentFormContext,
    getCurrentPatientTimeline,
    formContext,
    patientTimeline,
  };
  const read = useRef(latest);
  read.current = latest;

  useEffect(() => {
    let unmounted = false;

    void (async () => {
      const [formContextNow, patientTimelineNow] = await Promise.all([
        readOrFallback(
          () => read.current.getCurrentFormContext(),
          read.current.formContext,
        ),
        readOrFallback(
          () => read.current.getCurrentPatientTimeline(),
          read.current.patientTimeline,
        ),
      ]);

      if (unmounted) return;

      setCurrent({
        formContext: formContextNow,
        patientTimeline: patientTimelineNow,
      });
    })();

    return () => {
      unmounted = true;
    };
  }, []);

  if (!current?.patientTimeline) {
    return (
      <Stack
        sx={{ alignItems: "center", my: 4 }}
        data-testid="loading-current-supporting-documents"
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <FormSupportingDocumentsSelector
      {...selectorProps}
      formContext={current.formContext}
      patientTimeline={current.patientTimeline}
    />
  );
}
